import React, { useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail
} from 'firebase/auth';
import { auth } from '@festpix/services';
import { createUserProfile } from '@festpix/services';
import { createWalletForUser } from '@festpix/services';
import { colors, spacing, borderRadius, typography } from '@festpix/ui';

export default function LoginScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (isSignUp && !name.trim()) {
      setError('Por favor, insira seu nome');
      return;
    }

    setLoading(true);
    try {
      if (isSignUp) {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const userName = name.trim();
        await createUserProfile(
          userCredential.user.uid,
          email,
          userName,
          'attendee'
        );
        await createWalletForUser(
          userCredential.user.uid,
          email,
          userName
        );
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setError(null);
    setMessage(null);

    if (!email) {
      setError('Por favor, insira seu e-mail');
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('Verifique sua caixa de entrada para redefinir sua senha.');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <img src="/logo.png" alt="FestPix" style={styles.logo} />

      <form onSubmit={handleAuth} style={styles.form}>
        {isSignUp && (
          <input
            type="text"
            placeholder="Nome completo"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={styles.input}
            autoCapitalize="words"
          />
        )}

        <input
          type="email"
          placeholder="E-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.input}
          autoCapitalize="none"
        />

        <input
          type="password"
          placeholder="Senha"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={styles.input}
        />

        {error && <p style={styles.errorText}>{error}</p>}
        {message && <p style={styles.successText}>{message}</p>}

        <button
          type="submit"
          style={{
            ...styles.button,
            ...(loading ? styles.buttonDisabled : {})
          }}
          disabled={loading}
        >
          {loading ? 'Carregando...' : (isSignUp ? 'Cadastrar' : 'Entrar')}
        </button>
      </form>

      <button
        onClick={() => {
          setIsSignUp(!isSignUp);
          setName('');
          setError(null);
          setMessage(null);
        }}
        style={styles.linkButton}
      >
        {isSignUp ? 'Já tem uma conta? Entrar' : 'Não tem uma conta? Cadastrar'}
      </button>

      {!isSignUp && (
        <button onClick={handleForgotPassword} style={styles.forgotButton}>
          Esqueci minha senha
        </button>
      )}
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: spacing.lg,
    backgroundColor: colors.background,
  },
  logo: {
    width: 150,
    height: 150,
    objectFit: 'contain',
    marginBottom: spacing.md,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 360,
  },
  input: {
    border: `1px solid ${colors.border}`,
    padding: spacing.md,
    marginBottom: spacing.md,
    borderRadius: borderRadius.md,
    fontSize: typography.fontSize.md,
    backgroundColor: colors.background,
    color: colors.text,
    outline: 'none',
  },
  button: {
    backgroundColor: colors.primary,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    color: colors.textLight,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    marginTop: spacing.sm,
    cursor: 'pointer',
    border: 'none',
  },
  buttonDisabled: {
    opacity: 0.7,
    cursor: 'not-allowed',
  },
  linkButton: {
    color: colors.primary,
    background: 'none',
    border: 'none',
    marginTop: spacing.lg,
    cursor: 'pointer',
    fontSize: typography.fontSize.md,
  },
  forgotButton: {
    color: colors.info,
    background: 'none',
    border: 'none',
    marginTop: spacing.md,
    cursor: 'pointer',
    fontSize: typography.fontSize.sm,
  },
  errorText: {
    color: colors.error,
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  successText: {
    color: colors.success,
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
};
